// src/shared/services/RealService.js
import { CognitoIdentityProviderClient, InitiateAuthCommand, GetUserCommand, ForgotPasswordCommand, ConfirmForgotPasswordCommand } from "@aws-sdk/client-cognito-identity-provider";
import ServiceInterface from './ServiceInterface';
import apiService from './apiService';
import { ServiceError, ErrorTypes } from './types';

export const cognitoClient = new CognitoIdentityProviderClient({
    region: process.env.REACT_APP_COGNITO_REGION
});

class RealService extends ServiceInterface {

    constructor() {
        super();
        this.currentUser = null;
    }

    // Auth methods
    async refreshTokens() {
        const refreshToken = localStorage.getItem('RefreshToken');
        if (!refreshToken) {
            throw new Error('Refresh token is not available');
        }

        const params = {
            AuthFlow: "REFRESH_TOKEN_AUTH",
            ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
            AuthParameters: {
                REFRESH_TOKEN: refreshToken,
            },
        };

        try {
            const command = new InitiateAuthCommand(params);
            const { AuthenticationResult } = await cognitoClient.send(command);
            if (AuthenticationResult) {
                localStorage.setItem("IdToken", AuthenticationResult.IdToken || '');
                localStorage.setItem("AccessToken", AuthenticationResult.AccessToken || '');
                // Note: RefreshToken is not returned on refresh, keep the existing one
                return AuthenticationResult;
            }
        } catch (error) {
            console.error("Error refreshing token: ", error);
            // Clear tokens on refresh failure
            localStorage.removeItem("IdToken");
            localStorage.removeItem("AccessToken");
            localStorage.removeItem("RefreshToken");
            throw error;
        }
    }

    async getCurrentUser() {
        const accessToken = localStorage.getItem('AccessToken');
        if (!accessToken) {
            throw new Error('Access token is not available');
        }

        const fetchUser = async (token) => {
            const params = { AccessToken: token };
            const command = new GetUserCommand(params);
            return await cognitoClient.send(command);
        };

        try {
            const UserResult = await fetchUser(accessToken);
            if (UserResult) {
                return UserResult;
            }
        } catch (error) {
            // Check if token expired (Cognito returns NotAuthorizedException)
            if (error.name === 'NotAuthorizedException' || error.__type === 'NotAuthorizedException') {
                try {
                    // Try to refresh the token
                    const newTokens = await this.refreshTokens();
                    if (newTokens?.AccessToken) {
                        // Retry with new token
                        const UserResult = await fetchUser(newTokens.AccessToken);
                        if (UserResult) {
                            return UserResult;
                        }
                    }
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);
                    this.currentUser = null;
                    return null;
                }
            }

            if (error.response?.status === 404) {
                throw new ServiceError(
                    ErrorTypes.NOT_FOUND,
                    'User not found'
                );
            }

            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to fetch user data'
            );
        }
    }

    async login(credentials) {
        const username = credentials.email
        const password = credentials.password
        const params = {
            AuthFlow: "USER_PASSWORD_AUTH",
            ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
            AuthParameters: {
                USERNAME: username,
                PASSWORD: password,
            },
        };
        try {
            const command = new InitiateAuthCommand(params);
            const { AuthenticationResult } = await cognitoClient.send(command);
            if (AuthenticationResult) {
                return AuthenticationResult;
            }
        } catch (error) {
            console.error("Error signing in: ", error);
            throw error;
        }
    }

    async logout() {
        try {
            localStorage.removeItem("IdToken");
            localStorage.removeItem("AccessToken");
            localStorage.removeItem("RefreshToken");
        } catch (error) {
            throw error;
        }
    }

    async forgotPassword(email) {
        const params = {
            ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
            Username: email,
        };
        try {
            const command = new ForgotPasswordCommand(params);
            await cognitoClient.send(command);
        } catch (error) {
            console.error("Error initiating password reset: ", error);
            throw error;
        }
    }

    async confirmForgotPassword(email, code, newPassword) {
        const params = {
            ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
            Username: email,
            ConfirmationCode: code,
            Password: newPassword,
        };
        try {
            const command = new ConfirmForgotPasswordCommand(params);
            await cognitoClient.send(command);
        } catch (error) {
            console.error("Error confirming password reset: ", error);
            throw error;
        }
    }

    async signUp(credentials) {
        console.log(credentials)
        try {
            const response = await apiService.post('/signup', credentials);
            return {
                success: true,
                message: "Sign up successful. Please check your email to confirm your account.",
                data: response.data
            };
        } catch (error) {
            console.error("Error signing up with backend: ", error);
            throw error;
        }
    }



    // Activity methods
    async getActivities(nextPageToken = undefined, pageSize = 10, username = null, exemptionStatus = null) {
        try {
            const paramsObj = { nextPageToken, pageSize };
            if (username) {
                paramsObj.username = username;
            }
            if (exemptionStatus) {
                paramsObj.exemptionStatus = exemptionStatus;
            }
            const filteredParams = Object.fromEntries(
                Object.entries(paramsObj).filter(([key, value]) => value !== undefined && value !== null)
            );
            const params = new URLSearchParams(filteredParams);
            const response = await apiService.get(`/activities?${params.toString()}`);
            return {
                activities: response.data,
                nextPageToken: response?.lastEvaluatedKey,
                hasMore: !response.isLast
            };
        } catch (error) {
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to fetch activities'
            );
        }
    }

    async createActivity(activityData) {
        try {
            console.log('Registering activity with data:', activityData);
            const response = await apiService.post('/activities', {
                ...activityData,
                attachments: activityData.attachments.map(attachment => ({
                    name: attachment.name,
                    type: attachment.type
                }))
            });
            const activity = response.data;
            if (activity.attachments && activity.attachments.length > 0) {
                for (const attachment of activity.attachments) {
                    const originalAttachment = activityData.attachments.find(a => a.name === attachment.name);
                    const { url, fileType } = attachment;
                    console.log('Uploading attachment:', originalAttachment.file);
                    try {
                        const uploadResponse = await apiService.put(url, originalAttachment.file, {
                            headers: {
                                'Content-Type': fileType,
                            }
                        });
                        console.log('Upload response:', uploadResponse);
                    } catch (uploadError) {
                        console.error('Error uploading attachment:', uploadError);
                        console.error('Attachment URL:', url);
                        console.error('Attachment file:', originalAttachment.file);
                    }
                }
            }
            return response;
        } catch (error) {
            console.error('Error registering activity:', error);
            throw error;
        }
    }

    async fetchActivity(activityId) {
        try {
            const response = await apiService.get(`/activities?activityId=${activityId}`);
            if (!Array.isArray(response.data) || response.data.length !== 1) {
                throw new ServiceError(
                    ErrorTypes.INVALID_RESPONSE,
                    'Expected a single activity object in response'
                );
            }
            return response.data[0];
        } catch (error) {
            console.error('Error fetching activity:', error);
            if (error instanceof ServiceError) {
                throw error;
            } else {
                throw new ServiceError(
                    ErrorTypes.SERVER,
                    error.response?.data?.message || 'Failed to fetch activity'
                );
            }
        }
    }

    async updateActivity(activityId, activityData) {
        try {
            // Get existing activity to get existing attachments
            const existingActivity = await this.fetchActivity(activityId);
            const existingAttachments = existingActivity.attachments || [];
            // Update activity data without attachments
            const response = await apiService.put(`/activities/${activityId}`, {
                ...activityData,
                attachments: activityData.attachments.map(attachment => ({
                    name: attachment.name,
                    type: attachment.type
                }))
            });
            const updatedActivity = response.data;

            // Identify new attachments that need to be uploaded
            const newAttachments = updatedActivity.attachments.filter(attachment => !existingAttachments.some(existingAttachment => existingAttachment.name === attachment.name));

            // Upload new attachments
            for (const attachment of newAttachments) {
                const originalAttachment = activityData.attachments.find(a => a.name === attachment.name);
                const { url, fileType } = attachment;
                console.log('Uploading attachment:', originalAttachment.file);
                try {
                    const uploadResponse = await apiService.put(url, originalAttachment.file, {
                        headers: {
                            'Content-Type': fileType,
                        }
                    });
                    console.log('Upload response:', uploadResponse);
                } catch (uploadError) {
                    console.error('Error uploading attachment:', uploadError);
                    console.error('Attachment URL:', url);
                    console.error('Attachment file:', originalAttachment.file);
                }
            }
            return response;
        } catch (error) {
            console.error('Error updating activity:', error);
            throw error;
        }
    }

    async deleteActivity(activityId) {
        try {
            const response = await apiService.delete(`/activities/${activityId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting activity:', error);
            throw error;
        }
    }

    async deleteActivityAsAdmin(activityId) {
        try {
            const response = await apiService.delete(`/admin/activities/${activityId}`);
            return response.data;
        } catch (error) {
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to delete activity'
            );
        }
    }

    async reactToActivity(activityId, emoji) {
        try {
            return await apiService.post(`/activities/${activityId}/reactions`, { emoji });
        } catch (error) {
            console.error('Error reacting to activity:', error);
            throw error;
        }
    }

    async analyzeActivityImage(imageFile, imageName, activityType = null) {
        /**
         * Analyze an activity image to extract workout metrics.
         *
         * Full flow:
         * 1. Get presigned S3 URL for temp upload
         * 2. Upload image to S3 temp location
         * 3. Call analyze endpoint with S3 URL
         * 4. Return suggestions
         */
        try {
            console.log('Starting image analysis for:', imageName);

            // Step 1: Get presigned URL for temp upload
            console.log('Requesting presigned URL...');
            const presignedResponse = await apiService.post('/attachments/presigned-url', {
                fileName: imageName,
                contentType: imageFile.type,
                temp: true  // Upload to temp/ prefix
            });

            if (!presignedResponse.url || !presignedResponse.key) {
                throw new Error('Failed to get presigned URL');
            }

            const { url: presignedUrl, key: s3Key, bucket } = presignedResponse;
            console.log('Got presigned URL for key:', s3Key);

            // Step 2: Upload image to S3 using presigned URL
            console.log('Uploading image to S3...');

            // Use XMLHttpRequest instead of fetch to avoid any interceptors
            // that might be adding Authorization headers
            const uploadSuccess = await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                xhr.open('PUT', presignedUrl, true);
                xhr.setRequestHeader('Content-Type', imageFile.type);

                xhr.onload = function() {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        console.log('S3 upload successful');
                        resolve(true);
                    } else {
                        console.error('S3 upload failed:', xhr.status, xhr.responseText);
                        reject(new Error(`S3 upload failed: ${xhr.status} - ${xhr.responseText}`));
                    }
                };

                xhr.onerror = function() {
                    console.error('S3 upload network error');
                    reject(new Error('Network error during S3 upload'));
                };

                xhr.send(imageFile);
            });

            if (!uploadSuccess) {
                throw new Error('S3 upload failed');
            }

            console.log('Image uploaded successfully');

            // Step 3: Call analyze endpoint with S3 URL
            console.log('Calling analyze endpoint...');
            const s3Url = `s3://${bucket}/${s3Key}`;
            const response = await apiService.post('/activities/analyze-image', {
                imageUrl: s3Url,
                imageName: imageName,
                activityType: activityType
            });

            console.log('Analysis complete:', response);
            return response;

        } catch (error) {
            console.error('Error analyzing image:', error);
            // Don't throw - return error response so form can continue
            return {
                success: false,
                error: 'ANALYSIS_FAILED',
                message: error.message || 'Failed to analyze image',
                suggestions: null
            };
        }
    }

    async requestActivityExemption(activityId, note) {
        try {
            const response = await apiService.post(`/activities/${activityId}/exemption`, { note });
            return response.data;
        } catch (error) {
            console.error('Error requesting activity exemption:', error);
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to request activity exemption'
            );
        }
    }

    async reviewActivityExemption(activityId, action, note) {
        try {
            const response = await apiService.put(`/activities/${activityId}/exemption`, { action, note });
            return response.data;
        } catch (error) {
            console.error('Error reviewing activity exemption:', error);
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to review activity exemption'
            );
        }
    }

    // Competition methods
    async getAllCompetitions(state = null) {
        try {
            if (state) {
                // Fetch competitions for a specific state
                const response = await apiService.get(`/competitions?state=${state}`);
                return response.competitions;
            }

            // Fetch all competitions by querying all states in parallel
            const [active, upcoming, concluded] = await Promise.all([
                apiService.get('/competitions?state=active'),
                apiService.get('/competitions?state=upcoming'),
                apiService.get('/competitions?state=concluded')
            ]);

            return [
                ...active.competitions,
                ...upcoming.competitions,
                ...concluded.competitions
            ];
        } catch (error) {
            console.error('Error fetching competitions:', error);
            throw error;
        }
    }

    async fetchCompetition(competitionId) {
        try {
            const response = await apiService.get(`/competitions?competitionId=${competitionId}`);
            return response.competitions;
        } catch (error) {
            console.error('Error fetching competitions:', error);
            throw error;
        }
    }

    async getCompetitionDetails(competitionId) {
        try {
            const response = await apiService.get(`/competitions?detailed=true&competitionId=${competitionId}`);
            return {
                competition: response.competitions[0],
                isUserJoined: false
            };
        } catch (error) {
            console.error('Error fetching competitions:', error);
            throw error;
        }
    }

    async joinCompetition(competitionId) {
        try {
            const response = await apiService.post(`/competitions/${competitionId}/join`);
            return response.data;
        } catch (error) {
            console.error(`Error joining competition with ID ${competitionId}:`, error);
            throw error;
        }
    }

    async createCompetition(competitionData) {
        try {
            const response = await apiService.post('/competitions', {
                name: competitionData.name,
                pointTarget: competitionData.pointTarget,
                startDate: competitionData.startDate,
                endDate: competitionData.endDate,
                isTeamBased: competitionData.isTeamBased ?? false,
            });
            return response;
        } catch (error) {
            console.error('Error creating competition:', error);
            throw error;
        }
    }

    async updateCompetition(competitionId, competitionData) {
        try {
            const response = await apiService.put(`/competitions/${competitionId}`, competitionData);
            return response;
        } catch (error) {
            console.error('Error updating competition:', error);
            throw error;
        }
    }

    async reassignTeamMember(competitionId, username, teamId) {
        try {
            const response = await apiService.patch(`/competitions/${competitionId}/teams/members`, {
                username,
                teamId: teamId || null,
            });
            return response;
        } catch (error) {
            console.error('Error reassigning team member:', error);
            throw error;
        }
    }

    async joinTeam(competitionId, action, { teamId, teamName } = {}) {
        try {
            const body = action === 'create' ? { action, teamName } : { action, teamId };
            const response = await apiService.post(`/competitions/${competitionId}/teams/join`, body);
            return response;
        } catch (error) {
            console.error('Error joining team:', error);
            throw error;
        }
    }

    async getCompetitionStats(competitionId) {
        try {
            const response = await apiService.get(`/competitions/${competitionId}/statistics`);
            return response;
        } catch (error) {
            console.error('Error fetching competition statistics:', error);
            throw error;
        }
    }
    async getCompetitorHistory(competitionId, username) { throw new Error('Not implemented'); }
    async getCompetitionRules() { throw new Error('Not implemented'); }

    // Profile methods
    async fetchProfile(username) {
        try {
            const response = await apiService.get(`/profiles/${username}`);
            return response.data;
        } catch (error) {
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to fetch profile'
            );
        }
    }

    async getProfile(username) {
        try {
            const response = await apiService.get(`/users/${username}`);

            return response;
        } catch (error) {
            console.error(`Error getting profile for user ${username}:`, error);
            if (error instanceof ServiceError) {
                throw error;
            } else {
                throw new ServiceError(
                    ErrorTypes.SERVER,
                    error.response?.data?.message || 'Failed to fetch profile'
                );
            }
        }
    }

    async updateProfile(username, profileData) {
        try {
            console.log('Updating profile with data:', profileData);
            const response = await apiService.put(`/users/${username}`, profileData);
            return response.data;
        } catch (error) {
            console.error('Error updating profile:', error);
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to update profile'
            );
        }
    }

    async getAllUsers({ pageSize = 20, nextToken = null, filters = {} } = {}) {
        try {
            const params = new URLSearchParams({ pageSize: String(pageSize) });
            if (nextToken) params.append('nextToken', nextToken);
            Object.entries(filters).forEach(([key, value]) => {
                if (value) params.append(key, 'true');
            });
            const response = await apiService.get(`/users?${params.toString()}`);
            return {
                users: response.users || [],
                nextToken: response.nextToken || null
            };
        } catch (error) {
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to fetch users'
            );
        }
    }

    // Strava methods
    async getStravaStatus() {
        try {
            const response = await apiService.get('/strava/status');
            return response;
        } catch (error) {
            console.error('Error fetching Strava status:', error);
            return { connected: false };
        }
    }

    async connectStrava() {
        try {
            const response = await apiService.get('/strava/auth');
            if (response.authUrl) {
                window.location.href = response.authUrl;
            }
            return response;
        } catch (error) {
            console.error('Error initiating Strava connection:', error);
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to connect Strava'
            );
        }
    }

    async handleStravaCallback(code, state) {
        try {
            const response = await apiService.get(`/strava/callback?code=${code}&state=${state}`);
            return response;
        } catch (error) {
            console.error('Error handling Strava callback:', error);
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to complete Strava connection'
            );
        }
    }

    async disconnectStrava() {
        try {
            const response = await apiService.delete('/strava/auth');
            return response;
        } catch (error) {
            console.error('Error disconnecting Strava:', error);
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to disconnect Strava'
            );
        }
    }

    // Dashboard methods
    async getFitHubData() {
        try {
            const response = await apiService.get('/dashboard');
            return response.data;
        } catch (error) {
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to fetch FitHub data'
            );
        }
    }

    // Admin methods
    async getAdminStats() {
        try {
            const response = await apiService.get('/admin/stats');
            return response;
        } catch (error) {
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to fetch admin stats'
            );
        }
    }

    async getAdminExemptions({ status, pageSize = 20, nextToken } = {}) {
        try {
            const params = new URLSearchParams();
            if (status) params.set('status', status);
            if (pageSize) params.set('pageSize', String(pageSize));
            if (nextToken) params.set('nextToken', nextToken);

            const response = await apiService.get(`/admin/exemptions?${params.toString()}`);
            return response;
        } catch (error) {
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to fetch admin exemptions'
            );
        }
    }

    async reviewExemption(activityId, { action, note } = {}) {
        try {
            const response = await apiService.put(`/activities/${activityId}/exemption`, { action, note });
            return response;
        } catch (error) {
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to review activity exemption'
            );
        }
    }

    async getExemptionCounts() {
        try {
            const response = await apiService.get('/admin/exemptions/counts');
            return response;
        } catch (error) {
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to fetch exemption counts'
            );
        }
    }

    async syncCompetitionStates() {
        try {
            const response = await apiService.post('/admin/competitions/sync-states', {});
            return response.data;
        } catch (error) {
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to sync competition states'
            );
        }
    }

    async forceCompetitionState(competitionId, state) {
        try {
            const response = await apiService.put(`/admin/competitions/${competitionId}/state`, { state });
            return response.data;
        } catch (error) {
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || 'Failed to update competition state'
            );
        }
    }

    async adminResyncStrava(username, after, before = null, overwrite = false, ignorePenalty = true) {
        try {
            return await apiService.post('/admin/strava/resync', { username: username || undefined, after, before: before || undefined, overwrite, ignorePenalty });
        } catch (error) {
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || error.message || 'Failed to resync Strava activities'
            );
        }
    }

    async adminGetStravaAthletes() {
        try {
            return await apiService.get('/admin/strava/athletes');
        } catch (error) {
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || error.message || 'Failed to fetch connected athletes'
            );
        }
    }

    async adminVerifyStravaAthlete(username) {
        try {
            return await apiService.get(`/admin/strava/athletes/${username}`);
        } catch (error) {
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || error.message || 'Verification failed'
            );
        }
    }

    async adminGetStravaQuota() {
        try {
            return await apiService.get('/admin/strava/quota');
        } catch (error) {
            throw new ServiceError(
                ErrorTypes.SERVER,
                error.response?.data?.message || error.message || 'Failed to fetch Strava quota'
            );
        }
    }
}

export default RealService;